import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Alert,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatCurrency,
  formatPct,
  formatDate,
} from './shared';

interface ChannelData {
  days: number;
  margins_used: Record<string, number>;
  channels: {
    channel: string;
    orders: number;
    customers: number;
    revenue: number;
    avg_order_value: number;
    margin_pct: number;
    estimated_profit: number;
    revenue_share: number;
    profit_share: number;
  }[];
  totals: { revenue: number; estimated_profit: number };
  trend: { date: string; channel: string; revenue: number; orders: number }[];
}

const CHANNEL_LABELS: Record<string, string> = {
  storefront: '官網（metanoia.tw）',
  shopee: 'Shopee',
  outlet: '實體 Outlet',
  unknown: '其他',
};

const CHANNEL_COLORS: Record<string, string> = {
  storefront: COLORS.storefront,
  shopee: COLORS.shopee,
  outlet: COLORS.outlet,
  unknown: COLORS.slate,
};

const ChannelMarginSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [days, setDays] = useState<number>(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChannelData | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/api/retention/channel-margin?days=${days}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [days, refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!data) return <SectionError />;

  // Pivot trend data to stack by channel
  const trendMap = new Map<string, any>();
  data.trend.forEach((t) => {
    const key = t.date;
    if (!trendMap.has(key)) {
      trendMap.set(key, { date: key, dateLabel: formatDate(key) });
    }
    trendMap.get(key)[t.channel] = t.revenue;
  });
  const trendData = Array.from(trendMap.values()).sort((a, b) =>
    a.date < b.date ? -1 : 1
  );

  const channels = data.channels;
  const storefrontChannel = channels.find((c) => c.channel === 'storefront');
  const shopeeChannel = channels.find((c) => c.channel === 'shopee');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={30}>過去 30 天</MenuItem>
            <MenuItem value={90}>過去 90 天</MenuItem>
            <MenuItem value={180}>過去 180 天</MenuItem>
            <MenuItem value={365}>過去 365 天</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
        利潤估算依預設毛利率：官網 {(data.margins_used.storefront * 100).toFixed(0)}%、Shopee{' '}
        {(data.margins_used.shopee * 100).toFixed(1)}%、Outlet{' '}
        {(data.margins_used.outlet * 100).toFixed(0)}%。實際毛利視商品組成而定。
      </Alert>

      {/* Headline */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="總營收"
            value={formatCurrency(data.totals.revenue)}
            subtitle={`過去 ${data.days} 天`}
            color={COLORS.primary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="預估總毛利"
            value={formatCurrency(data.totals.estimated_profit)}
            subtitle={
              data.totals.revenue > 0
                ? `整體毛利率 ${formatPct(
                    (data.totals.estimated_profit / data.totals.revenue) * 100
                  )}`
                : '—'
            }
            color={COLORS.success}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="官網營收佔比"
            value={storefrontChannel ? formatPct(storefrontChannel.revenue_share) : '—'}
            subtitle={
              storefrontChannel
                ? `${formatCurrency(storefrontChannel.revenue)} · 毛利佔 ${formatPct(
                    storefrontChannel.profit_share
                  )}`
                : '—'
            }
            color={COLORS.storefront}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Shopee 營收佔比"
            value={shopeeChannel ? formatPct(shopeeChannel.revenue_share) : '—'}
            subtitle={
              shopeeChannel
                ? `${formatCurrency(shopeeChannel.revenue)} · 毛利佔 ${formatPct(
                    shopeeChannel.profit_share
                  )}`
                : '—'
            }
            color={COLORS.shopee}
          />
        </Grid>
      </Grid>

      {/* Channel breakdown table */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="通路明細（營收 vs 預估毛利）"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader="營收佔比與毛利佔比落差大 → 高營收低毛利，該調整重心"
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>通路</TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    訂單數
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    客戶數
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    營收
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    AOV
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    毛利率
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    預估毛利
                  </TableCell>
                  <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                    營收/毛利佔比
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channels.map((c) => {
                  const color = CHANNEL_COLORS[c.channel] || COLORS.slate;
                  return (
                    <TableRow key={c.channel} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={CHANNEL_LABELS[c.channel] || c.channel}
                          sx={{
                            bgcolor: `${color}22`,
                            color,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatNumber(c.orders)}</TableCell>
                      <TableCell align="right">{formatNumber(c.customers)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(c.revenue)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(c.avg_order_value)}</TableCell>
                      <TableCell align="right">{formatPct(c.margin_pct)}</TableCell>
                      <TableCell align="right" sx={{ color: COLORS.success, fontWeight: 600 }}>
                        {formatCurrency(c.estimated_profit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                        {formatPct(c.revenue_share)} / {formatPct(c.profit_share)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Trend area chart */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="通路營收趨勢"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="dateLabel" fontSize={11} />
              <YAxis
                fontSize={11}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip formatter={(v: any) => formatCurrency(v as number)} />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              {Object.keys(CHANNEL_COLORS).map((ch) =>
                channels.find((c) => c.channel === ch) ? (
                  <Area
                    key={ch}
                    type="monotone"
                    dataKey={ch}
                    name={CHANNEL_LABELS[ch]}
                    stackId="1"
                    stroke={CHANNEL_COLORS[ch]}
                    fill={CHANNEL_COLORS[ch]}
                    fillOpacity={0.6}
                  />
                ) : null
              )}
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChannelMarginSection;
